// First words with you (alphabet) — live preview (covers + photo + dedication + 26 letter pages).
// Ports the SAME render logic as the print generator (alphabet-book/generator/render.js) so the
// preview is WYSIWYG with print. Asset + coord URLs are injected by the template via window.ALPHABET.
// Reuses personalizationDB (loadProfile/saveProfile/deleteProfile).
(function () {
  const A = window.ALPHABET || {};

  // Artwork key for a gender + skin tone: boy | boy_dark | girl | girl_dark.
  // Every lookup falls back to the light set, so a missing dark asset degrades to
  // the light picture instead of rendering a blank page (mirrors the backend).
  function charKey(gender, tone) {
    const base = gender === "GIRL" ? "girl" : "boy";
    return tone === "dark" ? base + "_dark" : base;
  }
  function coverAsset(gender, tone, cover) {
    const base = gender === "GIRL" ? "girl" : "boy";
    return A["cover_" + charKey(gender, tone) + "_" + cover] || A["cover_" + base + "_" + cover];
  }
  const FRONT = { hard: { w: 2783, h: 3024 }, soft: { w: 2783, h: 2822 } };
  const COVER_SCALE = 4.62;   // display width = full cover px / COVER_SCALE  (~602px, matches Bedtime/Blessed)
  const PAGE = 2551;          // photo + dedication interior page px (square, matches generator)
  const LETTER_PAGE = 1160;   // letter-page canvas px (crisp at ~580px display)
  let LET, NAME_COORDS, EX;   // parsed coord JSONs
  const cache = {};
  const loadImg = (src) => cache[src] || (cache[src] = new Promise((res, rej) => { const im = new Image(); im.crossOrigin = "anonymous"; im.onload = () => res(im); im.onerror = rej; im.src = src; }));

  const fill = (line, name) => (line || "").replace(/\{name\}/g, name || "");

  // ---- INTERIOR letter pages: exact port of render.js drawLetter / drawSentence ----
  // Letter: tight-bbox centred on (cx,cy) — matches editor.html / compose.py.
  function drawLetter(ctx, ch, cx, cy, colour, K) {
    const size = LET.LETTER_SIZE * K;
    ctx.font = `${size}px ComingSoon`;
    ctx.fillStyle = colour;
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
    const m = ctx.measureText(ch);
    const asc = m.actualBoundingBoxAscent, desc = m.actualBoundingBoxDescent;
    const left = m.actualBoundingBoxLeft, right = m.actualBoundingBoxRight;
    const bx = cx - (right - left) / 2;
    const by = cy + (asc - desc) / 2;
    ctx.fillText(ch, bx, by);
  }
  // Sentence: multi-line block, centred on (cx,cy).
  function drawSentence(ctx, lines, cx, cy, K) {
    const F = LET.TEXT_SIZE * K;
    const step = LET.TEXT_SIZE * LET.LINE_SPACING * K;
    ctx.font = `${F}px Newsreader`;
    ctx.fillStyle = LET.TEXT_COLOR;
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    const met = lines.map((l) => ctx.measureText(l));
    const asc = (m) => m.actualBoundingBoxAscent, desc = (m) => m.actualBoundingBoxDescent;
    const lastH = asc(met[met.length - 1]) + desc(met[met.length - 1]);
    const total = step * (lines.length - 1) + lastH;
    let top = cy - total / 2;
    lines.forEach((ln, i) => { ctx.fillText(ln, cx, top + asc(met[i])); top += step; });
  }

  // Generic top-anchored, centred multi-line with auto-shrink (dedication + back-cover blurb).
  function drawLinesTop(ctx, lines, c, opts) {
    opts = opts || {};
    let F = c.fontSize, LH = c.lineHeight;
    const widest = () => { ctx.font = `${F}px Newsreader`; return Math.max(0, ...lines.map((l) => ctx.measureText(l).width)); };
    if (opts.maxWidth) { const w = widest(); if (w > opts.maxWidth) { const k = opts.maxWidth / w; F *= k; LH *= k; } }
    if (opts.maxBottom) { const bottom = c.y0 + (lines.length - 1) * LH; if (bottom > opts.maxBottom) { const k = (opts.maxBottom - c.y0) / ((lines.length - 1) * LH || 1); F *= k; LH *= k; } }
    ctx.fillStyle = c.colour; ctx.textAlign = "center"; ctx.textBaseline = "alphabetic"; ctx.font = `${F}px Newsreader`;
    lines.forEach((ln, i) => ctx.fillText(ln, c.cx, c.y0 + i * LH));
  }

  // ---- COVER: front panel + name (Gloria Hallelujah), NO "with" (title baked into panel) ----
  async function renderCover(canvasId, cover, name, gender, tone) {
    const cv = document.getElementById(canvasId); if (!cv) return;
    const F = FRONT[cover]; cv.width = F.w; cv.height = F.h; const ctx = cv.getContext("2d");
    cv.style.width = Math.round(F.w / COVER_SCALE) + "px"; cv.style.height = "auto";
    const g = gender === "GIRL" ? "girl" : "boy";   // name colour is per gender, not per tone
    ctx.drawImage(await loadImg(coverAsset(gender, tone, cover)), 0, 0, F.w, F.h);
    const f = NAME_COORDS.formats[cover]; const colour = NAME_COORDS.colour[g];
    let size = f.name_size;
    ctx.font = `${size}px GloriaHallelujah`;
    const w = ctx.measureText(name).width; if (w > f.max_width) size *= f.max_width / w;
    ctx.font = `${size}px GloriaHallelujah`;
    ctx.fillStyle = colour; ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
    ctx.fillText(name, f.cx, f.cy);
  }

  // ---- BACK: shared panel + personalised blurb (per-format coords) ----
  async function renderBack(canvasId, cover, name) {
    const cv = document.getElementById(canvasId); if (!cv) return;
    const F = FRONT[cover]; cv.width = F.w; cv.height = F.h; const ctx = cv.getContext("2d");
    // display width handled by .back-cover-image / .hard-back-cover-image CSS (responsive) — do NOT
    // set an inline width here, or it overrides the mobile width:100% and overflows on small screens.
    ctx.drawImage(await loadImg(A["back_" + cover]), 0, 0, F.w, F.h);
    const b = EX.back_blurb[cover]; const lines = EX.back_blurb_lines.map((l) => fill(l, name));
    drawLinesTop(ctx, lines, { cx: b.cx, y0: b.y0, fontSize: b.font_size, lineHeight: b.line_height, colour: b.colour });
  }

  // ---- PHOTO: bg + photo cover-fit into photo_box (plain, no frame) ----
  async function renderPhoto(profile) {
    const cv = document.getElementById("photoCanvas"); if (!cv) return;
    cv.width = PAGE; cv.height = PAGE; const ctx = cv.getContext("2d"); cv.style.width = "100%"; cv.style.height = "auto";
    ctx.drawImage(await loadImg(A.photo_bg), 0, 0, PAGE, PAGE);
    const b = EX.photo_box;
    if (profile && profile.imageBase64) {
      const p = await loadImg(profile.imageBase64);
      ctx.save(); ctx.beginPath(); ctx.rect(b.x, b.y, b.w, b.h); ctx.clip();
      const s = Math.max(b.w / p.width, b.h / p.height), dw = p.width * s, dh = p.height * s;
      ctx.drawImage(p, b.x + (b.w - dw) / 2, b.y + (b.h - dh) / 2, dw, dh); ctx.restore();
    }
  }

  // ---- DEDICATION: bg + text (block vertically centred on page middle, auto-shrink) ----
  async function renderDed(profile) {
    const cv = document.getElementById("messageCanvas"); if (!cv) return;
    cv.width = PAGE; cv.height = PAGE; const ctx = cv.getContext("2d"); cv.style.width = "100%"; cv.style.height = "auto";
    ctx.drawImage(await loadImg(A.dedication_bg), 0, 0, PAGE, PAGE);
    const d = EX.dedication, name = (profile && profile.name) || "";
    const raw = (profile && profile.dedicationText) || "";
    const lines = raw.split("\n").map((l) => fill(l, name));
    const y0 = d.y0 - ((lines.length - 1) * d.line_height) / 2;
    drawLinesTop(ctx, lines, { cx: d.cx, y0, fontSize: d.font_size, lineHeight: d.line_height, colour: d.colour }, { maxWidth: PAGE - 2 * 300, maxBottom: PAGE - 320 });
  }

  // ---- 26 letter pages: base illustration + letter + sentence (WYSIWYG) ----
  async function renderLetters(name, gender, tone) {
    const grid = document.getElementById("letter-pages"); if (!grid || !LET) return;
    grid.innerHTML = "";
    const base = gender === "GIRL" ? "girl" : "boy";
    const pages = (A.pages && (A.pages[charKey(gender, tone)] || A.pages[base])) || {};
    const P = LETTER_PAGE, K = P / (LET.SRC_CANVAS || 2816);
    const order = (A.letters && A.letters.length) ? A.letters : Object.keys(LET.letters);
    let col = 0; // alternate left/right so each 2-col row gets the spine shadow down the middle (matches Bedtime)
    for (const L of order) {
      const info = LET.letters[L]; if (!info) continue;
      const side = col % 2 === 0 ? "story-page-left" : "story-page-right";
      const wrap = document.createElement("div"); wrap.className = "story-page " + side;
      const cv = document.createElement("canvas"); cv.width = P; cv.height = P; cv.style.width = "100%"; cv.style.height = "auto";
      const ctx = cv.getContext("2d");
      const url = pages[L]; if (url) { try { ctx.drawImage(await loadImg(url), 0, 0, P, P); } catch (e) {} }
      drawLetter(ctx, L, info.lcx * P, info.lcy * P, info.color, K);
      drawSentence(ctx, info.lines.map((l) => fill(l, name)), info.tcx * P, info.tcy * P, K);
      wrap.appendChild(cv); grid.appendChild(wrap); col++;
    }
  }

  async function renderAll() {
    const profile = await loadProfile(); const name = profile && profile.name, g = profile && profile.gender;
    const tone = (profile && profile.skinTone) || "light";
    if (!name || !g) { window.location.replace("/pages/personalise-book-alphabet"); return; }
    const cn = document.getElementById("child-name"); if (cn) cn.innerText = `For: ${name}`;
    const rn = document.getElementById("recipient-name"); if (rn) rn.innerText = name;
    await renderCover("coverCanvas", "soft", name, g, tone);
    await renderCover("hardCoverCanvas", "hard", name, g, tone);
    await renderBack("backCanvas", "soft", name);
    await renderBack("hardBackCanvas", "hard", name);
    await renderPhoto(profile); await renderDed(profile);
    await renderLetters(name, g, tone);
  }

  function wireModals() {
    const openPhoto = document.getElementById("personalize-photo-btn"), photoModal = document.getElementById("photo-modal");
    const openMsg = document.getElementById("write-dedication-btn"), msgModal = document.getElementById("message-modal");
    const fileIn = document.getElementById("customImage"), prev = document.getElementById("photo-preview");
    const pc = document.getElementById("photo-preview-container"), ua = document.getElementById("upload-area"), rm = document.getElementById("remove-photo");
    function showPhotoPreview(src) { if (prev) { prev.src = src; prev.style.display = "block"; } if (pc) pc.style.display = "flex"; if (rm) rm.style.display = "block"; if (ua) ua.style.display = "none"; }
    function resetPhotoUI() { if (prev) { prev.src = ""; prev.style.display = "none"; } if (rm) rm.style.display = "none"; if (ua) ua.style.display = "block"; if (fileIn) fileIn.value = ""; }
    // Downscale to <=1600px and re-encode as a REAL PNG. The alphabet backend rejects non-PNG
    // uploads ("Only real PNG images are allowed"), so we must output image/png here.
    function resizeImageFile(file, maxDim) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file); const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(url);
          const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
          const w = Math.max(1, Math.round(img.width * scale)), h = Math.max(1, Math.round(img.height * scale));
          const cv = document.createElement("canvas"); cv.width = w; cv.height = h;
          cv.getContext("2d").drawImage(img, 0, 0, w, h);
          resolve(cv.toDataURL("image/png"));
        };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode failed")); };
        img.src = url;
      });
    }
    if (openPhoto && photoModal) { openPhoto.onclick = async () => { const p = await loadProfile(); if (p && p.imageBase64) { showPhotoPreview(p.imageBase64); } else { resetPhotoUI(); } photoModal.style.display = "flex"; }; }
    if (openMsg && msgModal) { openMsg.onclick = async () => { const p = await loadProfile(); const ta = document.getElementById("customMessage"); if (ta) ta.value = (p && p.dedicationText) || ""; msgModal.style.display = "flex"; }; }
    const cP = document.getElementById("close-photo-modal"); if (cP) cP.onclick = () => photoModal.style.display = "none";
    const cM = document.getElementById("close-message-modal"); if (cM) cM.onclick = () => msgModal.style.display = "none";
    if (fileIn) {
      fileIn.onchange = async () => {
        const f = fileIn.files[0]; if (!f) return;
        try { showPhotoPreview(await resizeImageFile(f, 1600)); }
        catch (e) { const r = new FileReader(); r.onload = (ev) => showPhotoPreview(ev.target.result); r.readAsDataURL(f); }
      };
    }
    const savePhoto = document.getElementById("save-photo"); if (savePhoto) { savePhoto.onclick = async () => { const p = await loadProfile(); const src = prev && prev.src; if (src && src.startsWith("data:")) { await saveProfile({ ...p, imageBase64: src }); } photoModal.style.display = "none"; renderAll(); }; }
    if (rm) { rm.onclick = async () => { const p = await loadProfile(); await saveProfile({ ...p, imageBase64: "" }); resetPhotoUI(); renderAll(); }; }
    const saveMsg = document.getElementById("save-message"); if (saveMsg) {
      saveMsg.onclick = async () => {
        const p = await loadProfile(); const ta = document.getElementById("customMessage"); const txt = (ta && ta.value) || "";
        if (txt.length > 400) { const er = document.getElementById("message-error"); if (er) { er.textContent = "Please keep your message under 400 characters."; er.style.display = "block"; } return; }
        await saveProfile({ ...p, dedicationText: txt }); msgModal.style.display = "none"; renderAll();
      };
    }
  }

  document.addEventListener("DOMContentLoaded", async function () {
    try {
      const [lj, nc, ex] = await Promise.all([
        fetch(A.coords_letters).then((r) => r.json()),
        fetch(A.coords_name).then((r) => r.json()),
        fetch(A.coords_extras).then((r) => r.json()),
      ]);
      LET = lj; NAME_COORDS = nc; EX = ex;
      try { await document.fonts.load("300px ComingSoon"); await document.fonts.load("300px GloriaHallelujah"); await document.fonts.load("60px Newsreader"); } catch (e) {}
      wireModals();
      const cont = document.getElementById("continue-to-format-btn"); if (cont) cont.onclick = () => window.location.href = "/pages/format-alphabet";
      await renderAll();
    } catch (e) { console.error("Alphabet preview error", e); }
  });
})();
